if (!customElements.get('beds-addons')) {
  customElements.define(
    'beds-addons',
    class BedsAddons extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-addon-row]'));

        this.boundOnClick = this.onClick.bind(this);
        this.boundOnChange = this.onChange.bind(this);
        // modal-dialog teleports itself to document.body on connect, so listeners live
        // on document rather than `this` to still catch clicks/changes inside the modal.
        document.addEventListener('click', this.boundOnClick);
        document.addEventListener('change', this.boundOnChange);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundOnClick);
        document.removeEventListener('change', this.boundOnChange);
      }

      ownsRow(row) {
        return !!row && this.rows.includes(row);
      }

      findRow(rowId) {
        return this.rows.find((row) => row.dataset.addonId === rowId);
      }

      // Lets other components (e.g. beds-variant-selector syncing the mattress add-on to the
      // chosen bed size) select a row's Nth option, matching by position rather than name.
      // Handles both row shapes: a dropdown (single product, multiple variants) and a
      // modal/collection picker (radio items grouped into one or more collection tabs).
      selectVariantAtIndex(addonTitle, index) {
        const row = this.rows.find((row) => row.dataset.addonTitle === addonTitle);
        if (!row) return;

        const dropdown = row.querySelector('[data-addon-dropdown]');
        if (dropdown) {
          this.selectDropdownRowAtIndex(row, dropdown, index);
          return;
        }

        this.selectCollectionRowAtIndex(row, index);
      }

      selectDropdownRowAtIndex(row, dropdown, index) {
        const target = Array.from(dropdown.options).filter((option) => option.value)[index];
        if (!target) return;

        dropdown.value = target.value;
        this.selectRow(row, target.value, target.dataset.price, this.readDetails(target));
      }

      // Each collection item is its own product with its own size variants (carried as JSON
      // in data-variants, in the same order as the bed's Size option). Syncing to a new size
      // index re-points every item at its Nth variant — the *product* picked in the modal
      // never changes, only which of that product's variants is currently in play.
      selectCollectionRowAtIndex(row, index) {
        const modal = document.querySelector(`modal-dialog[data-addon-row-id="${row.dataset.addonId}"]`);
        if (!modal) return;

        const items = Array.from(modal.querySelectorAll('.beds-addon-picker__item-input'));
        if (!items.length) return;

        let selectedItem = null;
        let selectedVariant = null;

        items.forEach((item) => {
          const variants = this.itemVariants(item);
          if (!variants.length) return;

          const isSelected = variants.some((variant) => String(variant.id) === row.dataset.selectedVariantId);
          const target = variants[index];
          if (!target) return;

          item.value = target.id;
          item.dataset.price = target.price;
          item.dataset.priceFormatted = target.priceFormatted;
          item.dataset.variantTitle = target.title;
          if (target.image) item.dataset.image = target.image;

          // Update every item's visible price to the size-matched variant, not just the
          // one currently selected — the modal is still open to browsing the rest.
          this.updateItemPriceDisplay(item, target);

          if (isSelected) {
            selectedItem = item;
            selectedVariant = target;
          }
        });

        if (!row.dataset.selectedVariantId || !selectedItem) return;

        this.selectRow(row, String(selectedVariant.id), selectedVariant.price, {
          image: selectedVariant.image,
          productTitle: selectedItem.dataset.productTitle,
          variantTitle: selectedVariant.title,
          priceFormatted: selectedVariant.priceFormatted,
        });
      }

      // Swaps the text of the already-rendered `price` snippet markup for this item to the
      // size-matched variant's precomputed values, rather than re-deriving the sale/compare-at
      // math in JS (that formatting logic lives once, in snippets/price.liquid).
      updateItemPriceDisplay(item, target) {
        const wrapper = item.closest('.beds-addon-picker__item')?.querySelector('[data-addon-item-price]');
        if (!wrapper) return;

        const saleEl = wrapper.querySelector('.price-item--sale');
        if (saleEl) saleEl.textContent = target.priceFormatted;

        const compareEl = wrapper.querySelector('.price-item--regular');
        if (compareEl) compareEl.textContent = target.compareAtFormatted;

        const badgeEl = wrapper.querySelector('.cstm-save-badge');
        if (badgeEl) badgeEl.textContent = `Save ${target.discountPercentage}%`;
      }

      itemVariants(item) {
        try {
          return JSON.parse(item.dataset.variants || '[]');
        } catch (error) {
          return [];
        }
      }

      onClick(event) {
        const modalAddButton = event.target.closest('[data-addon-modal-add]');
        if (modalAddButton) {
          this.handleModalAdd(modalAddButton);
          return;
        }

        const tabButton = event.target.closest('[data-addon-tab]');
        if (tabButton) {
          this.handleTabClick(tabButton);
          return;
        }

        const toggleButton = event.target.closest('[data-addon-toggle]');
        if (toggleButton && this.ownsRow(toggleButton.closest('[data-addon-row]'))) {
          this.handleToggle(toggleButton);
          return;
        }

        const removeButton = event.target.closest('[data-addon-remove]');
        if (removeButton && this.ownsRow(removeButton.closest('[data-addon-row]'))) {
          this.handleRemove(removeButton);
        }
      }

      onChange(event) {
        const dropdown = event.target.closest('[data-addon-dropdown]');
        if (dropdown) {
          this.handleDropdownChange(dropdown);
          return;
        }

        const input = event.target.closest('.beds-addon-picker__item-input');
        if (!input) return;

        const modal = input.closest('modal-dialog');
        if (!modal || !this.findRow(modal.dataset.addonRowId)) return;

        modal.querySelectorAll('.beds-addon-picker__item').forEach((item) => {
          item.classList.toggle('is-checked', item.contains(input) && input.checked);
        });
      }

      handleDropdownChange(dropdown) {
        const row = dropdown.closest('[data-addon-row]');
        if (!this.ownsRow(row)) return;

        const option = dropdown.options[dropdown.selectedIndex];
        if (!option.value) return;

        this.selectRow(row, option.value, option.dataset.price, this.readDetails(option));
      }

      handleModalAdd(button) {
        const modal = button.closest('modal-dialog');
        if (!modal) return;

        const row = this.findRow(modal.dataset.addonRowId);
        if (!row) return;

        const checked = modal.querySelector('.beds-addon-picker__item-input:checked');
        if (!checked) return;

        this.selectRow(row, checked.value, checked.dataset.price, this.readDetails(checked));
        modal.hide();
      }

      handleTabClick(button) {
        const modal = button.closest('modal-dialog');
        if (!modal || !this.findRow(modal.dataset.addonRowId)) return;

        const target = button.dataset.addonTab;
        modal.querySelectorAll('[data-addon-tab]').forEach((tab) => {
          tab.setAttribute('aria-selected', tab.dataset.addonTab === target ? 'true' : 'false');
        });
        modal.querySelectorAll('[data-addon-panel]').forEach((panel) => {
          panel.toggleAttribute('hidden', panel.dataset.addonPanel !== target);
        });
      }

      handleToggle(button) {
        const row = button.closest('[data-addon-row]');
        this.selectRow(row, button.dataset.addonVariantId, button.dataset.addonPrice, this.readDetails(button));
      }

      readDetails(source) {
        return {
          image: source.dataset.image,
          productTitle: source.dataset.productTitle,
          variantTitle: source.dataset.variantTitle,
          priceFormatted: source.dataset.priceFormatted,
        };
      }

      handleRemove(button) {
        const row = button.closest('[data-addon-row]');
        this.deselectRow(row);
      }

      selectRow(row, variantId, price, details) {
        row.dataset.selectedVariantId = variantId;
        row.dataset.selectedPrice = price || 0;
        this.updateVariantDisplay(row, details || null);
        this.toggleRowControls(row, true);
        this.dispatchChange();
      }

      deselectRow(row) {
        delete row.dataset.selectedVariantId;
        delete row.dataset.selectedPrice;
        this.updateVariantDisplay(row, null);
        this.toggleRowControls(row, false);

        const dropdown = row.querySelector('[data-addon-dropdown]');
        if (dropdown) dropdown.value = '';

        this.dispatchChange();
      }

      updateVariantDisplay(row, details) {
        const iconDefault = row.querySelector('[data-addon-icon-default]');
        const iconVariant = row.querySelector('[data-addon-icon-variant]');
        const copyDefault = row.querySelector('[data-addon-copy-default]');
        const copyVariant = row.querySelector('[data-addon-copy-variant]');
        if (!iconVariant || !copyVariant) return;

        if (details) {
          const image = iconVariant.querySelector('[data-addon-variant-image]');
          if (image && details.image) image.src = details.image;

          const productTitle = copyVariant.querySelector('[data-addon-product-title]');
          if (productTitle) productTitle.textContent = details.productTitle || '';

          const variantName = copyVariant.querySelector('[data-addon-variant-name]');
          if (variantName) variantName.textContent = details.variantTitle || '';

          const variantPrice = copyVariant.querySelector('[data-addon-variant-price]');
          if (variantPrice) variantPrice.textContent = details.priceFormatted || '';
        }

        iconDefault.hidden = !!details;
        iconVariant.hidden = !details;
        copyDefault.hidden = !!details;
        copyVariant.hidden = !details;
      }

      dispatchChange() {
        this.dispatchEvent(new CustomEvent('beds:selection-change', { bubbles: true }));
      }

      getSelectedTotal() {
        return this.rows
          .filter((row) => row.dataset.selectedVariantId)
          .reduce((total, row) => total + (Number(row.dataset.selectedPrice) || 0), 0);
      }

      toggleRowControls(row, selected) {
        const control = row.querySelector('[data-addon-control]');
        if (!control) return;

        const opener = control.querySelector('modal-opener');
        const dropdownGroup = control.querySelector('[data-addon-dropdown-group]');
        const toggleButton = control.querySelector('[data-addon-toggle]');
        const removeButton = control.querySelector('[data-addon-remove]');

        if (opener) opener.hidden = selected;
        if (dropdownGroup) dropdownGroup.hidden = selected;
        else if (toggleButton) toggleButton.hidden = selected;
        if (removeButton) removeButton.hidden = !selected;
      }

      getSelectedItems() {
        return this.rows
          .filter((row) => row.dataset.selectedVariantId)
          .map((row) => ({ id: row.dataset.selectedVariantId, quantity: 1 }));
      }
    }
  );
}


(function () {
  function initFocalPoints(container) {
    if (!container || container.dataset.focalInitialized === 'true') return;

    container.dataset.focalInitialized = 'true';

    const featureItems = container.querySelectorAll(
      '.focla_points_features_par li.focla_points_features_ch'
    );

    const imageFocalPoints = container.querySelectorAll(
      '.focal_points_image_conn span.focla_points_numbers'
    );

    const featureFocalPoints = container.querySelectorAll(
      '.focla_points_features_par span.focla_points_numbers'
    );

    const featureInfoItems = container.querySelectorAll(
      '.focal_point_info_mobile_conn .focla_points_features_info'
    );

    function clearActiveClasses() {
      imageFocalPoints.forEach(el => {
        el.classList.remove('focal_active');
      });

      featureFocalPoints.forEach(el => {
        el.classList.remove('focal_active');
      });

      featureInfoItems.forEach(el => {
        el.classList.remove('featureinfo-active');
      });
    }

    function activateFocalPoint(focalValue) {
      if (!focalValue) return;

      clearActiveClasses();

      imageFocalPoints.forEach(el => {
        if (el.dataset.focalNo === focalValue) {
          el.classList.add('focal_active');
        }
      });

      featureFocalPoints.forEach(el => {
        if (el.dataset.focalNo === focalValue) {
          el.classList.add('focal_active');
        }
      });

      featureInfoItems.forEach(el => {
        if (el.dataset.focalNo === focalValue) {
          el.classList.add('featureinfo-active');
        }
      });
    }

    featureItems.forEach(featureItem => {
      featureItem.addEventListener('click', function () {
        const focalValue =
          this.dataset.focalNo ||
          this.querySelector('.focla_points_numbers')?.dataset.focalNo;

        activateFocalPoint(focalValue);
      });
    });

    // Activate first item on load
    if (featureItems.length) {
      const firstFocalValue =
        featureItems[0].dataset.focalNo ||
        featureItems[0].querySelector('.focla_points_numbers')?.dataset.focalNo;

      activateFocalPoint(firstFocalValue);
    }
  }

  function initAllFocalPoints() {
    document.querySelectorAll(
      '[id*="focal_points"] .focal_points_image_and_features'
    ).forEach(initFocalPoints);
  }

  // Initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFocalPoints);
  } else {
    initAllFocalPoints();
  }

  // Watch for dynamically loaded/re-rendered content
  const observer = new MutationObserver(() => {
    initAllFocalPoints();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
